import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { api } from '../api/client';
import { toast } from 'sonner';

// Import SmartGrid for grid view
import SmartGrid from './SmartGrid';
import ProductDetailPage from './ProductDetailPage';

// Import Shadcn UI components
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';

// Icons
import { 
  Search, Filter, Download, Upload, Copy, Edit, Eye, Archive, 
  Trash2, MoreHorizontal, CheckCircle, Plus, Settings, Maximize2, Minimize2,
  RefreshCw, SortAsc, SortDesc, ChevronLeft, ChevronRight, ChevronDown, X
} from 'lucide-react';

const MOCK_TENANT_ID = 'tenant-123';

// ============================================================
// CELL RENDERERS - Clean styling with proper fallbacks
// ============================================================
const CellRenderers = {
  // Stock with color coding
  stock: (value) => {
    if (value === null || value === undefined) return <span className="text-gray-500">—</span>;
    const color = value === 0 ? 'text-red-400' : value < 10 ? 'text-orange-400' : 'text-white';
    return <span className={`${color} font-medium`}>{value}</span>;
  },
  
  // Price - show dash if zero/null
  price: (value) => {
    if (!value || value === 0) {
      return <span className="text-gray-500">—</span>;
    }
    return <span className="text-white">${parseFloat(value).toFixed(2)}</span>;
  },
  
  // Margin with color
  margin: (value) => {
    if (!value && value !== 0) return <span className="text-gray-500">—</span>;
    const numValue = parseFloat(value);
    const color = numValue < 15 ? 'text-red-400' : numValue < 25 ? 'text-orange-400' : 'text-green-400';
    return <span className={`${color} font-medium`}>{numValue.toFixed(1)}%</span>;
  },
  
  // Status - simple dot + text
  status: (value) => {
    const statusConfig = {
      ACTIVE: { color: 'bg-green-500', text: 'Active' },
      active: { color: 'bg-green-500', text: 'Active' },
      low_stock: { color: 'bg-orange-500', text: 'Low Stock' },
      out_of_stock: { color: 'bg-red-500', text: 'Out' },
      INACTIVE: { color: 'bg-gray-500', text: 'Inactive' },
      inactive: { color: 'bg-gray-500', text: 'Inactive' },
      ARCHIVED: { color: 'bg-gray-600', text: 'Archived' },
    };
    const config = statusConfig[value] || statusConfig.ACTIVE;
    return (
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${config.color}`}></span>
        <span className="text-gray-300 text-xs">{config.text}</span>
      </div>
    );
  },
};

// ============================================================
// PRODUCT MASTER COMPONENT - Clean Redesign
// ============================================================
const ProductMaster = ({ zenMode, setZenMode, navCollapsed, setNavCollapsed, onEditProduct }) => {
  // Core state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isZenMode, setIsZenMode] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list' - default to grid (SmartGrid)
  
  // Filters state
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: '',
    brand: '',
    channel: '',
    minStock: '',
    maxStock: '',
    lowMargin: false,
    sort: 'updated_desc',
    include_archived: false,
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 25,
    total_count: 0,
    total_pages: 0,
  });
  
  // Categories for filters
  const [categories, setCategories] = useState([]);
  
  // Effective costs cache
  const [effectiveCosts, setEffectiveCosts] = useState({});
  
  // Product detail modal state (local fallback)
  const [showEditDetail, setShowEditDetail] = useState(false);
  const [editDetailProductId, setEditDetailProductId] = useState(null);
  
  // Visible columns (for settings)
  const [visibleColumns, setVisibleColumns] = useState([
    'checkbox', 'sku', 'name', 'category', 'stock', 'cost', 'retail', 'margin', 'status', 'actions'
  ]);
  
  // All available columns
  const allColumns = [
    { key: 'checkbox', label: 'Select', width: '40px', fixed: true },
    { key: 'sku', label: 'SKU', width: '100px' },
    { key: 'name', label: 'Product Name', width: 'auto' },
    { key: 'category', label: 'Category', width: '120px' },
    { key: 'subcategory', label: 'Subcategory', width: '120px' },
    { key: 'stock', label: 'Stock', width: '80px', align: 'right' },
    { key: 'cost', label: 'Cost', width: '90px', align: 'right' },
    { key: 'retail', label: 'Retail', width: '90px', align: 'right' },
    { key: 'margin', label: 'Margin', width: '80px', align: 'right' },
    { key: 'status', label: 'Status', width: '100px' },
    { key: 'uom', label: 'UoM', width: '70px' },
    { key: 'updated', label: 'Updated', width: '100px' },
    { key: 'actions', label: '', width: '50px', fixed: true },
  ];
  
  // ============================================================
  // DATA FETCHING
  // ============================================================
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        tenant_id: MOCK_TENANT_ID,
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
      });
      
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.status) params.append('status', filters.status);
      if (filters.sort) params.append('sort', filters.sort);
      if (filters.include_archived) params.append('include_archived', 'true');
      
      const data = await api.get(`/api/v1/product-master/list?${params.toString()}`);
      setItems(data.items || []);
      setPagination(prev => ({
        ...prev,
        total_count: data.total_count || 0,
        total_pages: data.total_pages || 1,
      }));
      
      // Fetch effective costs for variants
      const variants = (data.items || []).filter(item => item.type === 'variant');
      if (variants.length > 0) {
        fetchEffectiveCosts(variants);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.page_size, filters]);
  
  const fetchEffectiveCosts = async (itemsList) => {
    try {
      const costPromises = itemsList.map(async (item) => {
        try {
          const data = await api.get(`/api/v1/product-master/variant/${item.id}/effective-cost`);
          return { id: item.id, ...data };
        } catch {
          return { id: item.id, effective_cost: null };
        }
      });
      const costs = await Promise.all(costPromises);
      const costsMap = {};
      costs.forEach(c => { costsMap[c.id] = c; });
      setEffectiveCosts(prev => ({ ...prev, ...costsMap }));
    } catch (error) {
      console.error('Error fetching effective costs:', error);
    }
  };
  
  const fetchCategories = useCallback(async () => {
    try {
      const data = await api.get(`/api/v1/categories?tenant_id=${MOCK_TENANT_ID}`);
      setCategories(data.categories || data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);
  
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);
  
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);
  
  // ============================================================
  // HANDLERS
  // ============================================================
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      status: '',
      brand: '',
      channel: '',
      minStock: '',
      maxStock: '',
      lowMargin: false,
      sort: 'updated_desc',
      include_archived: false,
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.category) count++;
    if (filters.status) count++;
    if (filters.brand) count++;
    if (filters.channel) count++;
    if (filters.minStock) count++;
    if (filters.maxStock) count++;
    if (filters.lowMargin) count++;
    return count;
  }, [filters]);
  
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItems(items.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };
  
  const handleSelectItem = (id, checked) => {
    if (checked) {
      setSelectedItems(prev => [...prev, id]);
    } else {
      setSelectedItems(prev => prev.filter(i => i !== id));
    }
  };
  
  const handleEditDetails = useCallback((item) => {
    if (!item || !item.id) {
      console.error('❌ handleEditDetails: Invalid item', item);
      return;
    }
    console.log('🔧 Opening product detail for:', item.sku || item.id);
    
    if (onEditProduct) {
      console.log('📤 Using parent onEditProduct callback');
      onEditProduct(item.id);
    } else {
      console.log('⚠️ Using local state (no parent callback)');
      setEditDetailProductId(item.id);
      setShowEditDetail(true);
    }
  }, [onEditProduct]);
  
  const handleSort = (field) => {
    const currentSort = filters.sort;
    let newSort;
    if (currentSort === `${field}_asc`) {
      newSort = `${field}_desc`;
    } else if (currentSort === `${field}_desc`) {
      newSort = 'updated_desc';
    } else {
      newSort = `${field}_asc`;
    }
    handleFilterChange('sort', newSort);
  };
  
  const getSortIcon = (field) => {
    if (filters.sort === `${field}_asc`) return <SortAsc className="w-3 h-3 text-blue-400" />;
    if (filters.sort === `${field}_desc`) return <SortDesc className="w-3 h-3 text-blue-400" />;
    return null;
  };
  
  const copySKU = (sku) => {
    navigator.clipboard.writeText(sku);
    toast.success('SKU copied to clipboard');
  };
  
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };
  
  const toggleColumn = (columnKey) => {
    if (columnKey === 'reset') {
      setVisibleColumns(['checkbox', 'sku', 'name', 'category', 'stock', 'cost', 'retail', 'margin', 'status', 'actions']);
      return;
    }
    setVisibleColumns(prev => 
      prev.includes(columnKey) 
        ? prev.filter(k => k !== columnKey)
        : [...prev, columnKey]
    );
  };
  
  // Calculate profit/margin for display
  const calculateMargin = (item) => {
    const cost = effectiveCosts[item.id]?.effective_cost || 0;
    const price = item.price || item.retail_price || 0;
    if (!cost || !price || cost === 0) return null;
    return ((price - cost) / price) * 100;
  };
  
  // ============================================================
  // ZEN MODE RENDER
  // ============================================================
  if (isZenMode) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0d1b2a] overflow-auto">
        {/* Zen Mode Header - Compact */}
        <div className="sticky top-0 bg-[#0d1b2a] border-b border-gray-800 px-4 py-1.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-white text-sm font-semibold leading-none">Product Master</h2>
            <span className="text-gray-500 text-xs">• {pagination.total_count} products • Zen Mode</span>
          </div>
          <Button 
            onClick={() => setIsZenMode(false)}
            variant="outline"
            size="sm"
            className="h-7 px-3 text-xs bg-[#1a2744] text-gray-300 border-gray-700 hover:bg-[#243656]"
          >
            <Minimize2 className="w-3 h-3 mr-1" />
            Exit
          </Button>
        </div>
        
        {/* Zen Mode Content */}
        <div className="p-6">
          {/* Filters */}
          <FilterBar 
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={clearFilters}
            activeFilterCount={activeFilterCount}
            showMoreFilters={showMoreFilters}
            setShowMoreFilters={setShowMoreFilters}
            categories={categories}
            loading={loading}
            onRefresh={fetchItems}
          />
          
          {/* Table */}
          <div className="mt-4">
            <ProductTable 
              items={items}
              loading={loading}
              selectedItems={selectedItems}
              onSelectAll={handleSelectAll}
              onSelectItem={handleSelectItem}
              onEditDetails={handleEditDetails}
              onSort={handleSort}
              getSortIcon={getSortIcon}
              copySKU={copySKU}
              effectiveCosts={effectiveCosts}
              calculateMargin={calculateMargin}
              visibleColumns={visibleColumns}
              allColumns={allColumns}
              isZenMode={true}
            />
          </div>
          
          {/* Pagination */}
          <TablePagination 
            pagination={pagination}
            onPageChange={handlePageChange}
            onPageSizeChange={(size) => setPagination(prev => ({ ...prev, page_size: size, page: 1 }))}
          />
        </div>
        
        {/* Modal */}
        {!onEditProduct && showEditDetail && ReactDOM.createPortal(
          <ProductDetailPage
            open={showEditDetail}
            onClose={() => { setShowEditDetail(false); setEditDetailProductId(null); }}
            productId={editDetailProductId}
            initialTab="general"
          />,
          document.body
        )}
      </div>
    );
  }
  
  // ============================================================
  // NORMAL RENDER
  // ============================================================
  return (
    <div className="p-4">
      {/* Compact Header - Single line ~30px */}
      <div className="flex items-center gap-2 py-1.5 mb-2 border-b border-gray-800">
        <h2 className="text-white text-sm font-semibold leading-none">Product Master</h2>
        <span className="text-gray-500 text-xs">• {pagination.total_count} products</span>
      </div>
      
      {/* Smart Grid View - Main product view */}
      <SmartGrid 
        zenMode={zenMode} 
        setZenMode={setZenMode}
        navCollapsed={navCollapsed}
        setNavCollapsed={setNavCollapsed}
      />
      
      {/* Modal (local fallback) */}
      {!onEditProduct && showEditDetail && ReactDOM.createPortal(
        <ProductDetailPage
          open={showEditDetail}
          onClose={() => { setShowEditDetail(false); setEditDetailProductId(null); }}
          productId={editDetailProductId}
          initialTab="general"
        />,
        document.body
      )}
    </div>
  );
};

// ProductMaster is now simplified - List view components moved below but not used

export default ProductMaster; 
